import { Text } from "@/components/ui/baseUi/text"
import { LoadingBlock } from "@/modules/boxDashboard/components/loadingBlock"
import { KPIPeriodPicker } from "@/modules/boxDashboard/modules/KPI/components/kpiPeriodPicker"
import { useKPI } from "@/modules/boxDashboard/modules/KPI/hooks/useKPI"
import {
  adjustComparisonMonthAfterFocusChange,
  createKPIPeriodModel,
  getDefaultKPIPeriod,
  isMonthDisabledForComparison,
  isMonthDisabledForFocus,
} from "@/modules/boxDashboard/modules/KPI/models/kpiPeriodModel"
import { ActivationsKpiBlock } from "@/modules/boxDashboard/modules/KPI/modules/activations/components/activationsKpiBlock"
import { createActivationsKpiModel } from "@/modules/boxDashboard/modules/KPI/modules/activations/models/activationsKpiModel"
import { IncomeKpiBlock } from "@/modules/boxDashboard/modules/KPI/modules/income/components/incomeKpiBlock"
import { createIncomeKpiModel } from "@/modules/boxDashboard/modules/KPI/modules/income/models/incomeKpiModel"
import { ActiveOffersKpiBlock } from "@/modules/boxDashboard/modules/KPI/modules/offers/components/activeOffersKpiBlock"
import { createActiveOffersKpiModel } from "@/modules/boxDashboard/modules/KPI/modules/offers/models/activeOffersKpiModel"
import { TrialsKpiBlock } from "@/modules/boxDashboard/modules/KPI/modules/trials/components/trialsKpiBlock"
import { createTrialsKpiModel } from "@/modules/boxDashboard/modules/KPI/modules/trials/models/trialsKpiModel"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { word } from "@modules/core/utils/i18n"
import dayjs, { type Dayjs } from "dayjs"
import { Suspense, useCallback, useMemo, useState } from "react"

export const KPI_TOOLTIP_DELAY = 700

export function KPIBlock() {
  const { result: box } = useCurrentBox()

  const boxCreationDate = useMemo(() => {
    if (!box?.createdAt) return null
    return dayjs(box.createdAt)
  }, [box])

  const [period, setPeriod] = useState(getDefaultKPIPeriod)

  const setFocusMonth = useCallback(
    (year: number, month: number) => {
      const newFocusMonth = dayjs().year(year).month(month).startOf("month")

      setPeriod((prev) => {
        const newComparisonMonth = adjustComparisonMonthAfterFocusChange(
          newFocusMonth,
          prev.comparison,
          boxCreationDate,
        )

        return {
          baseline: newFocusMonth,
          comparison: newComparisonMonth,
        }
      })
    },
    [boxCreationDate],
  )

  const setComparisonMonth = useCallback((year: number, month: number) => {
    const newComparisonMonth = dayjs().year(year).month(month).startOf("month")
    setPeriod((prev) => ({
      ...prev,
      comparison: newComparisonMonth,
    }))
  }, [])

  const locale = useLanguage()

  const KPIPeriodModel = useMemo(
    () => createKPIPeriodModel(period.baseline, period.comparison, locale),
    [period, locale],
  )

  const handleIsMonthDisabledForFocus = useCallback(
    (month: Dayjs) => isMonthDisabledForFocus(month, boxCreationDate),
    [boxCreationDate],
  )

  const handleIsMonthDisabledForComparison = useCallback(
    (month: Dayjs) => isMonthDisabledForComparison(month, period.baseline, boxCreationDate),
    [period.baseline, boxCreationDate],
  )

  return (
    <div className="flex flex-col rounded-4xl bg-surface-foreground pb-6">
      <div className="flex w-full items-start justify-between gap-4 px-6 pt-6">
        <Text fontStyle="HM" className="text-textDefault-heading">
          {word("boxDashboard.kpi.title")}
        </Text>

        <KPIPeriodPicker
          periodLabel={KPIPeriodModel.periodLabel}
          focusYear={KPIPeriodModel.focusYear}
          focusMonth={KPIPeriodModel.focusMonth}
          comparisonYear={KPIPeriodModel.comparisonYear}
          comparisonMonth={KPIPeriodModel.comparisonMonth}
          onFocusMonthChange={setFocusMonth}
          onComparisonMonthChange={setComparisonMonth}
          isMonthDisabledForFocus={handleIsMonthDisabledForFocus}
          isMonthDisabledForComparison={handleIsMonthDisabledForComparison}
        />
      </div>

      <Suspense fallback={<LoadingBlock className="mx-6 mt-6 h-242" />}>
        <KPIBlockData period={period} />
      </Suspense>
    </div>
  )
}

function KPIBlockData({ period }: { period: { baseline: Dayjs; comparison: Dayjs } }) {
  const { result: box } = useCurrentBox()
  const { kpi } = useKPI(period)
  const locale = useLanguage()

  const incomeKpiModel = useMemo(
    () => createIncomeKpiModel(kpi, period.baseline, period.comparison, locale),
    [kpi, period.baseline, period.comparison, locale],
  )

  const activeOffersKpiModel = useMemo(() => createActiveOffersKpiModel(kpi), [kpi])

  const activationsKpiModel = useMemo(() => createActivationsKpiModel(kpi), [kpi])

  const trialsKpiModel = useMemo(() => createTrialsKpiModel(kpi), [kpi])

  return (
    <div className="flex flex-col gap-10 px-6 pt-6">
      <IncomeKpiBlock model={incomeKpiModel} currency={box?.stripeAccountCurrency ?? "EUR"} locale={locale} />
      <ActiveOffersKpiBlock model={activeOffersKpiModel} />
      <ActivationsKpiBlock model={activationsKpiModel} />
      <TrialsKpiBlock model={trialsKpiModel} />
    </div>
  )
}
